import React, { useState, useEffect } from 'react';
import { Modal, Button, Select, Checkbox, Radio, Space, Spin, message } from 'antd';
import { DownloadOutlined, FileExcelOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import axios from 'axios';
import {get_cookie} from "../../helpers/get_cookie";


const { Option } = Select;

const ExcelExportButton = ({ onExport }) => {
    const [visible, setVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [stores, setStores] = useState([]);
    const [storeData, setStoreData] = useState([]);
    const [exportType, setExportType] = useState('all');
    const [storeType, setStoreType] = useState('1');
    const [specificStore, setSpecificStore] = useState(null);
    const [includeZero, setIncludeZero] = useState(false);
    const [includeWrittenOff, setIncludeWrittenOff] = useState(true);

    const instance = axios.create({
        baseURL: 'https://gdapp.org/API/4.0/',
        timeout: 10000,
    });

    useEffect(() => {
        if (visible) {
            fetchStoreData();
        }
    }, [visible]);

    const fetchStoreData = async () => {
        setLoading(true);
        try {
            const hash = get_cookie("hash");
            if (!hash) {
                message.error("API key not found in cookies");
                return;
            }

            const response = await instance.get(`store/get_store.php?api-key=${hash}`);

            if (response.data) {
                setStoreData(response.data.store || []);
                setStores(response.data.stores || []);
            }
        } catch (error) {
            console.error("Error fetching store data:", error);
            message.error("Ошибка загрузки данных");
        } finally {
            setLoading(false);
        }
    };

    const getStoresByType = (type) => {
        return stores.filter(store => Number(store.type) === type && store.enable === "1");
    };

    const getStoreName = (storeId) => {
        const store = stores.find(s => s.name_db === storeId);
        return store ? store.name : `Склад ${storeId}`;
    };

    const prepareDataForExport = () => {
        let filteredData = [...storeData];

        // Фильтрация по типу экспорта
        switch (exportType) {
            case 'type':
                const typeStores = getStoresByType(Number(storeType));
                const typeStoreIds = typeStores.map(store => store.name_db);

                filteredData = filteredData.map(item => {
                    const newItem = { ...item };

                    // Оставляем только склады выбранного типа
                    Object.keys(newItem).forEach(key => {
                        if (key.startsWith('store_')) {
                            const storeId = key.replace('store_', '');
                            if (!typeStoreIds.includes(storeId)) {
                                delete newItem[key];
                            }
                        }
                    });

                    return newItem;
                });
                break;

            case 'specific':
                if (specificStore) {
                    const store = stores.find(s => s.id === specificStore);
                    if (store) {
                        filteredData = filteredData.map(item => {
                            const newItem = { ...item };
                            const storeKey = `store_${store.name_db}`;
                            const storeValue = newItem[storeKey] || 0;

                            // Оставляем только выбранный склад
                            Object.keys(newItem).forEach(key => {
                                if (key.startsWith('store_') && key !== storeKey) {
                                    delete newItem[key];
                                }
                            });

                            newItem[storeKey] = storeValue;
                            return newItem;
                        });
                    }
                }
                break;

            case 'writtenOff':
                const writtenOffStores = getStoresByType(4);
                const writtenOffStoreIds = writtenOffStores.map(store => store.name_db);

                filteredData = filteredData.map(item => {
                    const newItem = { ...item };

                    // Оставляем только склады типа 4 (списанные)
                    Object.keys(newItem).forEach(key => {
                        if (key.startsWith('store_')) {
                            const storeId = key.replace('store_', '');
                            if (!writtenOffStoreIds.includes(storeId)) {
                                delete newItem[key];
                            }
                        }
                    });

                    return newItem;
                });
                break;
        }

        // Фильтрация нулевых позиций
        if (!includeZero) {
            filteredData = filteredData.filter(item => {
                return Object.keys(item).some(key => {
                    if (key.startsWith('store_')) {
                        const value = item[key];
                        const numericValue = typeof value === 'string' ?
                            parseFloat(value) || 0 : Number(value) || 0;
                        return numericValue > 0;
                    }
                    return false;
                });
            });
        }

        // Преобразование данных для Excel
        return filteredData.map(item => {
            const row = {
                'ID': item.id,
                'Наименование': item.name,
                'Бренд': item.brand,
                'Описание': item.description,
                'Ед. измерения': `${item.package} ${item.package_name}`,
                'Стоимость': item.cost,
                'Срок годности': item.date_avalible,
                'Категория': item.sub_category
            };

            // Добавляем колонки для каждого склада
            Object.keys(item).forEach(key => {
                if (key.startsWith('store_')) {
                    const storeId = key.replace('store_', '');
                    const storeName = getStoreName(storeId);
                    row[storeName] = item[key];
                }
            });

            return row;
        });
    };

    const exportToExcel = () => {
        const data = prepareDataForExport();

        if (data.length === 0) {
            message.warning('Нет данных для экспорта');
            return;
        }

        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Склад');

        // Настройка ширины колонок
        const colWidths = [
            { wch: 5 },  // ID
            { wch: 30 }, // Наименование
            { wch: 15 }, // Бренд
            { wch: 40 }, // Описание
            { wch: 15 }, // Ед. измерения
            { wch: 10 }, // Стоимость
            { wch: 12 }, // Срок годности
            { wch: 10 }, // Категория
            // Для складов будет автоматическая ширина
        ];
        worksheet['!cols'] = colWidths;

        XLSX.writeFile(workbook, 'export_sklad.xlsx');
        message.success('Данные экспортированы в Excel');
        setVisible(false);

        if (onExport) {
            onExport();
        }
    };

    const getExportOptions = () => {
        switch (exportType) {
            case 'type':
                return (
                    <Select
                        value={storeType}
                        onChange={setStoreType}
                        style={{ width: '100%' }}
                    >
                        <Option value="1">Основные склады</Option>
                        <Option value="2">Авто</Option>
                        <Option value="3">Сотрудники</Option>
                        <Option value="4">Списано</Option>
                    </Select>
                );

            case 'specific':
                const availableStores = stores.filter(store => store.enable === "1");
                return (
                    <Select
                        value={specificStore}
                        onChange={setSpecificStore}
                        style={{ width: '100%' }}
                        placeholder="Выберите склад"
                    >
                        {availableStores.map(store => (
                            <Option key={store.id} value={store.id}>
                                {store.name} ({store.type === '1' ? 'Основной' :
                                store.type === '2' ? 'Авто' :
                                    store.type === '3' ? 'Сотрудник' : 'Списано'})
                            </Option>
                        ))}
                    </Select>
                );

            default:
                return null;
        }
    };

    return (
        <>
            <Button
                icon={<FileExcelOutlined />}
                onClick={() => setVisible(true)}
                type="primary"
            >
                Экспорт в Excel
            </Button>

            <Modal
                title="Экспорт данных в Excel"
                open={visible}
                onCancel={() => setVisible(false)}
                footer={[
                    <Button key="cancel" onClick={() => setVisible(false)}>
                        Отмена
                    </Button>,
                    <Button
                        key="export"
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={exportToExcel}
                        disabled={loading || (exportType === 'specific' && !specificStore)}
                    >
                        Экспорт
                    </Button>
                ]}
                width={600}
            >
                <Spin spinning={loading}>
                    <Space direction="vertical" style={{ width: '100%' }} size="middle">
                        <div>
                            <div>Тип экспорта:</div>
                            <Radio.Group
                                value={exportType}
                                onChange={(e) => setExportType(e.target.value)}
                                style={{
                                    width: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '8px' // регулируйте отступ по необходимости
                                }}
                            >
                                <Radio value="all" style={{width: '300px'}}>Весь склад</Radio>
                                <Radio value="type"  style={{width: '300px'}}>По типу склада</Radio>
                                <Radio value="specific"  style={{width: '300px'}}>Конкретный склад</Radio>
                                <Radio value="writtenOff"  style={{width: '300px'}}>Только списанные</Radio>
                            </Radio.Group>
                        </div>

                        {exportType !== 'all' && exportType !== 'writtenOff' && (
                            <div>
                                <div>Параметры выбора:</div>
                                {getExportOptions()}
                            </div>
                        )}

                        <div>
                            <Space direction="vertical">
                                <Checkbox
                                    checked={includeZero}
                                    onChange={(e) => setIncludeZero(e.target.checked)}
                                >
                                    Включать позиции с нулевым остатком
                                </Checkbox>

                                {exportType !== 'writtenOff' && (
                                    <Checkbox
                                        checked={includeWrittenOff}
                                        onChange={(e) => setIncludeWrittenOff(e.target.checked)}
                                    >
                                        Включать списанные позиции
                                    </Checkbox>
                                )}
                            </Space>
                        </div>

                        <div style={{ color: '#666', fontSize: '12px' }}>
                            Будет создан файл со всеми выбранными данными в формате Excel
                        </div>
                    </Space>
                </Spin>
            </Modal>
        </>
    );
};

export default ExcelExportButton;